import React from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {  
  Email, 
  ErrorOutline, 
  MailOutline,
  ExpandMore,
  ExpandLess,
  Schedule
} from "@mui/icons-material";
import { formatDateWithTime } from "../../helpers";
import {
  IAcitvityEmailConversationLogsRightPanelProps,
} from "./Activity.types";
import {  LoadState } from "../../constants/enums";
import { useDispatchWrapper } from "../../hooks";
import { useSelector } from "react-redux";
import { IStoreState } from "../../redux/initialStoreState";
import { fetchEmailConversationListAsync } from "../../redux/activity/activityActions";
import { IEmailConversationActivity } from "../../redux/activity/activity.types";
import { RightPanel } from "../RightPanel";
import { CSS_CONSTANTS } from "../../constants/cssConstants";

export const ActivityEmailConversationLogsRightPanel: React.FC<
  IAcitvityEmailConversationLogsRightPanelProps
> = (props) => {
  const { moduleInformation, open, onClose } = props;
  const { list, loading, error } = useSelector(
    (storeState: IStoreState) => storeState.acitivty.emailConversation
  );

  const theme = useTheme();
  const dispatch = useDispatchWrapper();

  const fetchLogs = async () => {
    dispatch(fetchEmailConversationListAsync(moduleInformation.moduleId));
  };

  React.useEffect(() => {
    fetchLogs();
  }, []);

  return (
    <>
     <RightPanel
       heading="Email Conversation History"
       subHeading="View all email communications related to this record"
       open={open}
       onClose={onClose}
       width="50%"
     >
       <Stack spacing={1}>
       

      
         <Box sx={{ position: "relative" }}>
           {loading === LoadState.InProgress && (
             <Stack spacing={2}>
               {[1, 2, 3, 4].map((i) => (
                 <Card key={i} sx={{ borderRadius: 2 }}>
                   <CardContent>
                     <Stack spacing={1}>
                       <Skeleton variant="text" width="60%" height={30} />
                       <Skeleton variant="text" width="40%" height={20} />
                       <Skeleton variant="rectangular" height={60} />
                     </Stack>
                   </CardContent>
                 </Card>
               ))}
             </Stack>
           )}

           {loading === LoadState.Failed && error && (
             <Card
               sx={{
                 borderRadius: 2,
                 border: `1px solid ${theme.palette.error.light}`,
                 backgroundColor: theme.palette.error.light || "#fef2f2",
               }}
             >
               <CardContent>
                 <Box
                   display="flex"
                   flexDirection="column"
                   justifyContent="center"
                   alignItems="center"
                   minHeight={200}
                 >
                   <ErrorOutline
                     sx={{ fontSize: 48, color: theme.palette.error.main, mb: 2 }}
                   />
                   <Typography
                     variant="h6"
                     textAlign="center"
                     fontWeight={600}
                     color="error"
                   >
                     {error}
                   </Typography>
                 </Box>
               </CardContent>
             </Card>
           )}

           {loading === LoadState.Loaded && list.length === 0 && (
             <Card
               sx={{
                 borderRadius: 2,
                 border: `1px solid ${theme.palette.grey[300]}`,
                 backgroundColor: theme.palette.grey[50],
               }}
             >
               <CardContent>
                 <Box
                   display="flex"
                   flexDirection="column"
                   justifyContent="center"
                   alignItems="center"
                   minHeight={200}
                 >
                   <MailOutline
                     sx={{ fontSize: 64, color: theme.palette.grey[400], mb: 2 }}
                   />
                   <Typography
                     variant="h6"
                     textAlign="center"
                     fontWeight={600}
                     color="text.secondary"
                   >
                     No email conversations found
                   </Typography>
                   <Typography
                     variant="body2"
                     textAlign="center"
                     color="text.secondary"
                     mt={1}
                   >
                     Email communications will appear here
                   </Typography>
                 </Box>
               </CardContent>
             </Card>
           )}

           {loading === LoadState.Loaded && list.length > 0 && (
             <Stack spacing={2}>
               {list.map((item, index) => (
                 <Row key={index} data={item} isLast={index === list.length - 1} />
               ))}
             </Stack>
           )}
         </Box>
       </Stack>
     </RightPanel>
    </>
  );
};

const Row: React.FC<{ data: IEmailConversationActivity; isLast: boolean }> = ({ 
  data, 
  isLast 
}) => {
  const theme = useTheme();
  const [expanded, setExpanded] = React.useState(false);
  const [isHovered, setIsHovered] = React.useState(false);

  const handleToggle = () => {
    setExpanded(!expanded);
  };

  // Extract plain text from HTML content
  const getPlainText = (html: string) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || "";
  };

  const bodyPreview = data.body.content 
    ? getPlainText(data.body.content).slice(0, 150) + "..."
    : "No content";

  return (
    <Box
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        position: "relative",

      }}
    >

  

      <Card
        sx={{
          position: "relative",
          borderRadius: CSS_CONSTANTS.BORDER_RADIUS,
          mt: 0,
          mb: 0,
          border: expanded 
            ? `2px solid ${theme.palette.primary.main}`
            : `1px solid ${theme.palette.divider}`,
          boxShadow: expanded 
            ? theme.shadows[8]
            : isHovered 
            ? theme.shadows[2]
            : "none",
          transition: "all 0.2s ease-in-out",
          overflow: "hidden",
          backgroundColor: theme.palette.background.paper,
        }}
      >

        <CardContent 
          onClick={handleToggle}
          sx={{ 
            padding: "16px 20px !important",
            cursor: "pointer",
            backgroundColor: expanded 
              ? theme.palette.primary.main + "08"
              : "transparent",
            borderBottom: expanded ? `1px solid ${theme.palette.divider}` : "none",
            transition: "background-color 0.2s ease",
            "&:hover": {
              backgroundColor: expanded 
                ? theme.palette.primary.main + "08"
                : theme.palette.action.hover,
            }
          }}
        >
          <Stack direction="row" spacing={2}>
            {/* Icon Section */}
            <Box
              sx={{
                position: "relative",
                zIndex: 1,
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  backgroundColor: expanded 
                    ? theme.palette.primary.main
                    : theme.palette.primary.light,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.2s ease",
                }}
              >
                <Email
                  sx={{
                    color: expanded ? "#fff" : theme.palette.primary.main,
                    fontSize: 20,
                  }}
                />
              </Box>
            </Box>

            {/* Content Section */}
            <Stack flex={1} spacing={1}>
              {/* Header Row */}
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
              >
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    flex: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontSize: CSS_CONSTANTS.LABEL_HEADING_SIZE
                  }}
                >
                  {data.subject || "(No Subject)"}
                </Typography>

                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggle();
                  }}
                  sx={{
                    transition: "transform 0.2s ease",
                    transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                >
                  <ExpandMore />
                </IconButton>
              </Stack>

              {/* Date and Preview */}
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Schedule sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontWeight: 500,
                    }}
                  >
                    {formatDateWithTime(data.insert_ts)}
                  </Typography>
                </Stack>
                
                {!expanded && (
                  <>
                    <Typography variant="caption" color="text.disabled">•</Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.text.secondary,
                        flex: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {bodyPreview.slice(0, 60)}...
                    </Typography>
                  </>
                )}
              </Stack>
            </Stack>

          </Stack>
        </CardContent>


        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ padding: 3, backgroundColor: theme.palette.grey[50] }}>
            <Stack spacing={2.5}>
              <Box>
               
                <Stack spacing={1.5}>
                  {/* To Field */}
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 70, color: theme.palette.text.secondary, fontSize: CSS_CONSTANTS.LABEL_HEADING_SIZE }}>
                      To:
                    </Typography>
                    <Box sx={{ flex: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {data.to_mail_ids && data.to_mail_ids.length > 0 ? (
                        data.to_mail_ids.map((email: string, index: number) => (
                          <Chip 
                            label={email} 
                            key={index} 
                            size="small"
                            sx={{ fontSize: CSS_CONSTANTS.LABEL_VALUE_SIZE }}
                          />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: CSS_CONSTANTS.LABEL_VALUE_SIZE }}>--</Typography>
                      )}
                    </Box>
                  </Stack>

                  {/* CC Field */}
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 70, color: theme.palette.text.secondary, fontSize: CSS_CONSTANTS.LABEL_HEADING_SIZE }}>
                      CC:
                    </Typography>
                    <Box sx={{ flex: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {data.cc_mail_ids && data.cc_mail_ids.length > 0 ? (
                        data.cc_mail_ids.map((email: string, index: number) => (
                          <Chip 
                            label={email} 
                            key={index} 
                            size="small"
                            sx={{ fontSize: CSS_CONSTANTS.LABEL_VALUE_SIZE }}
                          />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: CSS_CONSTANTS.LABEL_VALUE_SIZE }}>--</Typography>
                      )}
                    </Box>
                  </Stack>

                  {/* BCC Field */}
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 70, color: theme.palette.text.secondary, fontSize: CSS_CONSTANTS.LABEL_HEADING_SIZE }}>
                      BCC:
                    </Typography>
                    <Box sx={{ flex: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {data.bcc_mail_ids && data.bcc_mail_ids.length > 0 ? (
                        data.bcc_mail_ids.map((email: string, index: number) => (
                          <Chip 
                            label={email} 
                            key={index} 
                            size="small"
                            sx={{ fontSize: CSS_CONSTANTS.LABEL_VALUE_SIZE }}
                          />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: CSS_CONSTANTS.LABEL_VALUE_SIZE }}>--</Typography>
                      )}
                    </Box>
                  </Stack>

                  {/* Subject Field */}
                  <Stack direction="row" spacing={1.5}>
                    <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 70, color: theme.palette.text.secondary, fontSize: CSS_CONSTANTS.LABEL_HEADING_SIZE }}>
                      Subject:
                    </Typography>
                    <Typography variant="body2" color="text.primary" sx={{ fontSize: CSS_CONSTANTS.LABEL_VALUE_SIZE }}>
                      {data.subject || "(No Subject)"}
                    </Typography>
                  </Stack>

                  
                </Stack>
              </Box>

              <Divider />

              {/* Email Body */}
              <Box>
             
                <Paper
                  elevation={0}
                  sx={{
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: CSS_CONSTANTS.BORDER_RADIUS,
                    padding: 2.5,
                 
                  }}
                >
                  <Box
                    sx={{
                      "& *": {
                        maxWidth: "100%",
                        wordWrap: "break-word",
                      },
                      "& img": {
                        maxWidth: "100% !important",
                        height: "auto !important",
                        width: "auto !important",
                        objectFit: "contain",
                      },
                      "& table": {
                        maxWidth: "100%",
                        width: "100% !important",
                        height: "auto !important",
                      },
                      lineHeight: 1.6,
                      color: theme.palette.text.primary,
                      fontSize: CSS_CONSTANTS.LABEL_VALUE_SIZE,
                    }}
                    dangerouslySetInnerHTML={{ __html: data.body.content || "No content available" }}
                  />
                </Paper>
              </Box>

              {/* Collapse Action */}
              <Stack direction="row" justifyContent="center">
                <Typography
                  variant="caption"
                  onClick={handleToggle}
                  sx={{
                    color: theme.palette.primary.main,
                    cursor: "pointer",
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  <ExpandLess sx={{ fontSize: 18 }} />
                  Collapse
                </Typography>
              </Stack>
            </Stack>
          </Box>
        </Collapse>
      </Card>
    </Box>
  );
};
